import OverallCompletionChart from './charts/OverallCompletionChart';
import CompletionOverTimeChart from './charts/CompletionOverTimeChart';
import CompletionByGroupChart from './charts/CompletionByGroupChart';
import ResponseDistributionChart from './charts/ResponseDistributionChart';
import SingleCompletionOverTimeChart from './charts/SingleCompletionOverTimeChart';
import OpenEndedInsightsChart from './charts/OpenEndedInsightsChart';
import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSurveyStatistics } from '../../../../../services/data-entry/survey-statistics.service';
import { SURVEY_TYPE_STANDARD } from '../../constants/typeSurvey';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tab,
  Tabs,
  IconButton,
  CircularProgress,
  Alert,
  Skeleton
} from '@mui/material';

const SurveyStatisticsModal = ({ open, onClose, surveyId, surveyName, type }) => {
  const [tabValue, setTabValue] = useState('overall');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statisticsData, setStatisticsData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Charts that support fullscreen mode
  const chartsWithFullscreen = ['overall', 'overtime', 'bygroup', 'distribution'];
  const closedQuestions = ['multiplechoice', 'singlechoice', 'ranking'];

  // Check if there are closed questions
  const distributionHasClosedQuestions = useMemo(() => {
    return (
      (statisticsData?.responseDistribution || []).filter((q) => closedQuestions.includes(q?.questionType))
        .length > 0
    );
  }, [statisticsData]);

  // Check if there are open-ended questions
  const hasOpenEndedQuestions = useMemo(() => {
    return (statisticsData?.responseWordCloud || []).length > 0;
  }, [statisticsData]);

  // Define tab configuration
  const tabConfig = [
    { key: 'overall', label: 'Overall Completion', icon: 'chart-pie', show: true },
    { key: 'overtime', label: 'Completion Over Time', icon: 'chart-line', show: true },
    { key: 'bygroup', label: 'Completion by Group', icon: 'chart-bar', show: type === SURVEY_TYPE_STANDARD },
    {
      key: 'distribution',
      label: 'Response Distribution for all closed questions',
      icon: 'chart-column',
      show: distributionHasClosedQuestions
    },
    {
      key: 'open-ended-insights',
      label: 'Open-Ended Insights',
      icon: 'cloud',
      show: hasOpenEndedQuestions
    }
  ];

  const onRefresh = async () => {
    try {
      setIsFirstLoad(false);
      const { data, error } = await getSurveyStatistics(surveyId);

      if (!error) {
        setStatisticsData(data);
      }
    } catch (err) {
      /* no log */
    }
  };

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setIsFullscreen(true);
      setError(null);

      const { data, error } = await getSurveyStatistics(surveyId);

      if (error) {
        setError('Failed to load survey statistics');
      } else {
        setStatisticsData(data);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setIsFirstLoad(true);
    setTabValue(newValue);
  };

  const handleClose = () => {
    setTabValue('overall'); // Reset to first tab key
    setStatisticsData(null);
    setError(null);
    onClose();
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    if (open && surveyId) {
      loadStatistics();
    }
  }, [open, surveyId]);

  const renderTabContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 3 }}>
          {/* Chart Header Skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant='text' width='40%' height={40} />
            <Skeleton variant='text' width='60%' height={24} sx={{ mt: 1 }} />
          </Box>

          {/* Chart Content Skeleton */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant='rounded' width='100%' height={400} />
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='600px'>
          <Alert severity='error' sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Box>
      );
    }

    if (!statisticsData) {
      return null;
    }

    switch (tabValue) {
      case 'overall':
        return (
          <OverallCompletionChart
            data={statisticsData?.overallCompletion}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        );
      case 'overtime':
        return type === SURVEY_TYPE_STANDARD ? (
          <CompletionOverTimeChart
            data={statisticsData?.completionOverTime}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        ) : (
          <SingleCompletionOverTimeChart
            data={statisticsData?.completionOverTime}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        );
      case 'bygroup':
        return (
          <CompletionByGroupChart
            data={statisticsData?.completionByGroup}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        );
      case 'distribution':
        return (
          <ResponseDistributionChart
            data={statisticsData?.responseDistribution}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        );
      case 'open-ended-insights':
        return (
          <OpenEndedInsightsChart
            data={statisticsData?.responseWordCloud}
            isFirstLoad={isFirstLoad}
            onRefresh={onRefresh}
          />
        );
      default:
        return null;
    }
  };

  const hideFullStatistics = Number(statisticsData?.pendingResponses) < 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isFullscreen ? false : 'lg'}
      fullWidth
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          minHeight: isFullscreen ? '100vh' : '600px',
          ...(isFullscreen && {
            m: 0,
            borderRadius: 0
          })
        }
      }}
    >
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography variant='h6' component='div'>
              {surveyName}
            </Typography>
          </Box>
          <Box display='flex' gap={1}>
            {/* Fullscreen Toggle Button - now available for all charts */}
            {statisticsData && chartsWithFullscreen.includes(tabValue) && (
              <IconButton
                onClick={toggleFullscreen}
                size='small'
                style={{ color: '#fff' }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                <FontAwesomeIcon icon={['fal', isFullscreen ? 'compress' : 'expand']} />
              </IconButton>
            )}
            <IconButton onClick={handleClose} size='small' style={{ color: '#fff' }}>
              <FontAwesomeIcon icon={['fal', 'times']} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          padding: isFullscreen ? '1rem 2rem' : '2rem',
          backgroundColor: '#fff',
          height: isFullscreen ? 'calc(100vh - 120px)' : 'auto',
          overflow: 'auto'
        }}
      >
        {/* Summary Statistics Section */}
        {loading ? (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f2f7fc', borderRadius: 1 }}>
            <Box display='flex' gap={4} justifyContent={'start'}>
              {[1, 2, 3, 4].map((item) => (
                <Box key={item} textAlign='center'>
                  <Skeleton variant='text' width={80} height={60} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          statisticsData && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f2f7fc', borderRadius: 1 }}>
              <Box display='flex' gap={4}>
                {!hideFullStatistics && (
                  <Box textAlign='center'>
                    <Typography variant='h4' color='primary'>
                      {statisticsData.completionRate}%
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Completion Rate
                    </Typography>
                  </Box>
                )}

                <Box textAlign='center'>
                  <Typography variant='h4' color='success.main'>
                    {statisticsData.completedResponses}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Completed
                  </Typography>
                </Box>

                {!hideFullStatistics && (
                  <Box textAlign='center'>
                    <Typography variant='h4' color='warning.main'>
                      {statisticsData.pendingResponses}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Pending
                    </Typography>
                  </Box>
                )}

                {!hideFullStatistics && (
                  <Box textAlign='center'>
                    <Typography variant='h4' color='info.main'>
                      {statisticsData.totalRespondents}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Total Respondents
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )
        )}

        {/* Tabs Section */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {loading ? (
            <Box display='flex' gap={2} p={1}>
              {[1, 2, 3, 4].map((item) => (
                <Skeleton key={item} variant='rounded' width={180} height={48} />
              ))}
            </Box>
          ) : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }
              }}
            >
              {tabConfig
                ?.filter((tab) => !!tab.show)
                .map((tab) => (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={tab.label}
                    icon={<FontAwesomeIcon icon={['fal', tab.icon]} />}
                    iconPosition='start'
                    sx={{ maxWidth: '400px' }}
                  />
                ))}
            </Tabs>
          )}
        </Box>

        {/* Chart Content */}
        <Box
          sx={{
            mt: 2,
            minHeight: isFullscreen ? 'calc(100vh - 200px)' : '600px',
            height: isFullscreen ? 'calc(100vh - 200px)' : 'auto'
          }}
        >
          {renderTabContent()}
        </Box>
      </DialogContent>

      {!isFullscreen && (
        <DialogActions>
          <Button onClick={handleClose} variant='outlined'>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SurveyStatisticsModal;
